import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const Toggle = ({ checked, onChange, testId }) => (
  <label className="relative inline-flex items-center cursor-pointer shrink-0">
    <input type="checkbox" data-testid={testId} checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only peer" />
    <div className="w-11 h-6 bg-gray-300 peer-checked:bg-black rounded-full peer transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
  </label>
);

export default function RemindersBlock({ API, t, salon }) {
  const [cfg, setCfg] = useState(null);
  const [log, setLog] = useState({ items: [], upcoming: 0 });
  const [saving, setSaving] = useState(false);
  const [channels, setChannels] = useState({ whatsapp: null, phone: null });

  useEffect(() => {
    if (!salon) return;
    setCfg({
      reminders_enabled: salon.reminders_enabled !== false,
      reminder_24h_enabled: salon.reminder_24h_enabled !== false,
      reminder_2h_enabled: salon.reminder_2h_enabled !== false,
      reminder_24h_text: salon.reminder_24h_text || "",
      reminder_2h_text: salon.reminder_2h_text || "",
    });
    axios.get(`${API}/reminders/log`).then((r) => setLog(r.data)).catch(() => {});
    Promise.all([
      axios.get(`${API}/greenapi/status`).then((r) => r.data).catch(() => ({})),
      axios.get(`${API}/phone/status`).then((r) => r.data).catch(() => ({})),
    ]).then(([wa, ph]) => setChannels({
      whatsapp: !!(wa.connected && (wa.state === "authorized" || !wa.state)),
      phone: !!(ph.number),
    }));
  }, [API, salon]);

  if (!cfg) return null;

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/salons/${salon.id}`, cfg);
      toast.success(t("mk.saved"));
    } catch { toast.error("Error"); }
    setSaving(false);
  };

  const row = (key, label, hint, textKey, ph) => (
    <div className="border border-gray-200 rounded-lg p-3" data-testid={`reminder-row-${key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold text-sm">{label}</div>
          <div className="text-xs text-gray-600 mt-0.5">{hint}</div>
        </div>
        <Toggle checked={cfg[`reminder_${key}_enabled`]} onChange={(v) => setCfg({ ...cfg, [`reminder_${key}_enabled`]: v })} testId={`reminder-${key}-toggle`} />
      </div>
      {cfg[`reminder_${key}_enabled`] && (
        <div className="mt-2">
          <Label className="text-xs font-medium">{t("mk.remindersTextLabel")}</Label>
          <textarea data-testid={`reminder-${key}-text`} value={cfg[textKey]} placeholder={ph}
            onChange={(e) => setCfg({ ...cfg, [textKey]: e.target.value })}
            className="w-full min-h-[64px] p-2.5 border-2 border-black rounded-lg text-sm mt-1" />
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-white border-2 border-black rounded-xl p-4 sm:p-6" data-testid="reminders-block">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg sm:text-xl font-semibold">⏰ {t("mk.remindersTitle")}</h3>
          <p className="text-sm text-gray-600 mt-1">{t("mk.remindersHint")}</p>
        </div>
        <Toggle checked={cfg.reminders_enabled} onChange={(v) => setCfg({ ...cfg, reminders_enabled: v })} testId="reminders-toggle" />
      </div>
      {cfg.reminders_enabled && (
        <div className="mt-4 space-y-3">
          {channels.whatsapp === false && (
            <div className="flex flex-wrap items-center gap-2 text-sm bg-amber-50 border border-amber-300 rounded-lg px-3 py-2" data-testid="reminders-no-whatsapp">
              <span>⚠️ {t("mk.remindersNoWhatsapp")}</span>
              <a href="/dashboard?tab=integrations&open=whatsapp" className="font-semibold underline" data-testid="reminders-connect-whatsapp">{t("mk.remindersConnect")} →</a>
            </div>
          )}
          {channels.phone === false && (
            <div className="flex flex-wrap items-center gap-2 text-sm bg-amber-50 border border-amber-300 rounded-lg px-3 py-2" data-testid="reminders-no-phone">
              <span>📞 {t("mk.remindersNoPhone")}</span>
              <a href="/dashboard?tab=integrations&open=voice" className="font-semibold underline" data-testid="reminders-connect-phone">{t("mk.remindersConnect")} →</a>
            </div>
          )}
          {channels.whatsapp && channels.phone && (
            <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="reminders-channels-ok">✓ {t("mk.remindersChannelsOk")}</div>
          )}
          {row("24h", t("mk.reminder24hTitle"), t("mk.reminder24hHint"), "reminder_24h_text", t("mk.reminder24hPh"))}
          {row("2h", t("mk.reminder2hTitle"), t("mk.reminder2hHint"), "reminder_2h_text", t("mk.reminder2hPh"))}
          <p className="text-xs text-gray-500">{t("mk.remindersPlaceholders")}</p>
        </div>
      )}
      <Button data-testid="mk-save-reminders-btn" onClick={save} disabled={saving} className="mt-4 bg-black text-white hover:bg-gray-800">{t("mk.save")}</Button>

      <div className="mt-5 border-t border-gray-200 pt-4">
        <div className="flex items-center justify-between gap-3 text-sm">
          <div className="font-semibold">{t("mk.remindersLogTitle")}</div>
          <div className="text-gray-600" data-testid="reminders-upcoming">{t("mk.remindersUpcoming", { n: log.upcoming })}</div>
        </div>
        {log.items.length === 0 ? (
          <p className="text-xs text-gray-500 mt-2" data-testid="reminders-log-empty">{t("mk.remindersLogEmpty")}</p>
        ) : (
          <div className="mt-2 divide-y divide-gray-100" data-testid="reminders-log">
            {log.items.map((it) => (
              <div key={it.id} className="py-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                <span className="font-mono text-gray-500">{(it.created_at || "").slice(0, 16).replace("T", " ")}</span>
                <span className="font-semibold">{it.kind}</span>
                <span className="truncate max-w-[40%]">{it.client_name || it.phone || "—"}</span>
                <span className="text-gray-500">→ {(it.start_at || "").slice(0, 16).replace("T", " ")}</span>
                <span className={`ml-auto px-1.5 py-0.5 rounded ${it.via ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>{it.via || t("mk.remindersNotDelivered")}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
